import React, { useState, useEffect } from 'react';
import { axiosInstance } from '@/axiosConfig';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  LineChart, Line, ComposedChart, Area
} from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Wallet } from 'lucide-react';
import { toast } from 'sonner';

export default function RelatoriosEvolucao() {
  const [ano, setAno] = useState(new Date().getFullYear().toString());
  const [dados, setDados] = useState([]);
  const [resumo, setResumo] = useState({ faturamento: 0, lucro: 0, caixa: 0 });
  const [loading, setLoading] = useState(false);

  const anosDisponiveis = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() - i).toString());

  useEffect(() => {
    fetchDados();
  }, [ano]);

  const fetchDados = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/relatorios/evolucao?ano=${ano}`);
      setDados(response.data.mensal);
      setResumo(response.data.total_anual);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar dados de evolução.");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
  };

  return (
    <div className="container mx-auto px-6 py-8 space-y-8">
      
      {/* Cabeçalho e Filtros */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pt-8">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Evolução Financeira
          </h1>
          <p className="text-gray-500">Acompanhe o crescimento da sua agência mês a mês.</p>
        </div>
        
        <div className="w-full md:w-48">
          <Label className="mb-2 block text-xs font-medium text-gray-500 uppercase">Ano de Referência</Label>
          <Select value={ano} onValueChange={setAno}>
            <SelectTrigger className="bg-white">
              <SelectValue placeholder="Selecione o ano" />
            </SelectTrigger>
            <SelectContent>
              {anosDisponiveis.map((a) => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Cards de Resumo Anual */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-l-4 border-l-blue-500 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Faturamento Total ({ano})</CardTitle>
            <TrendingUp className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(resumo.faturamento)}</div>
            <p className="text-xs text-gray-500 mt-1">Soma de todas as vendas e receitas</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Lucro Líquido ({ano})</CardTitle>
            <DollarSign className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(resumo.lucro)}</div>
            <p className="text-xs text-gray-500 mt-1">Resultado DRE (Competência)</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Saldo de Caixa (Realizado)</CardTitle>
            <Wallet className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(resumo.caixa)}</div>
            <p className="text-xs text-gray-500 mt-1">Entradas - Saídas efetivadas no ano</p>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Gráfico 1: DRE Mensal (Faturamento vs Despesas) */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle>Resultado Operacional (DRE)</CardTitle>
            <p className="text-sm text-gray-500">Comparativo mensal de Entradas vs Saídas (Competência)</p>
          </CardHeader>
          <CardContent className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dados} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="mes_nome" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} tickFormatter={(value) => `R$ ${value/1000}k`} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                  itemSorter={(item) => {
                    // Retorna -1 para "subir" o item na lista se for Receita/Faturamento
                    // Verifique se no seu código o dataKey é 'Receitas' ou se você mudou para 'Faturamento'
                    if (item.name === 'Faturamento' || item.dataKey === 'Receitas' || item.name === 'Receitas') {
                        return -1;
                    }
                    return 1;
                }}
                />
                <Legend />
                <Bar dataKey="faturamento" name="Faturamento" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="despesas" name="Despesas/Custos" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gráfico 2: Evolução do Lucro e Caixa */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle>Lucro vs Fluxo de Caixa</CardTitle>
            <p className="text-sm text-gray-500">Acompanhe a rentabilidade e a disponibilidade de dinheiro</p>
          </CardHeader>
          <CardContent className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dados} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="mes_nome" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} tickFormatter={(value) => `R$ ${value/1000}k`} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Legend />
                <Area type="monotone" dataKey="lucro" name="Lucro Líquido" fill="#dcfce7" stroke="#22c55e" strokeWidth={2} />
                <Line type="monotone" dataKey="saldo_caixa" name="Saldo Caixa (Realizado)" stroke="#8b5cf6" strokeWidth={3} dot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}